import {FC, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'

// Shown when the Backoffice API denies the session with 403 {type:'restricted'|'deactivate'}.
// Uses the app's own auth design system (same shell as AuthPage / Login / Deactivate — centered
// layout, logo, white card, metronic typography, i18n via react-i18next). Self-contained (no
// redux/Routes coupling) because AuthInit renders it before the app boots.
type Props = {
  type: 'restricted' | 'deactivate'
  onLogout: () => void
}

// Same set + symbols as AuthPage's language selector (react-i18next codes; 'ge' = Deutsch).
const LANGUAGES = [
  {symbol: 'en', name: 'English'},
  {symbol: 'ge', name: 'Deutsch'},
  {symbol: 'es', name: 'Español'},
  {symbol: 'pl', name: 'Polski'},
  {symbol: 'ru', name: 'Русский'},
]

// SSO tools a restricted user keeps access to (source of truth: the Keycloak `infinity` realm clients /
// their redirect URIs). A deactivated account has no access anywhere, so the list is hidden for it.
const TOOLS: {name: string; url: string}[] = [
  {name: 'Academy', url: 'https://academy.be-infinity.com'},
  {name: 'Compass', url: 'https://compass.be-infinity.com'},
  {name: 'Cockpit', url: 'https://cockpit.be-infinity.com'},
  {name: 'Tracker', url: 'https://tracker.be-infinity.com'},
  {name: 'Insights', url: 'https://insights.be-infinity.com'},
  {name: 'Shoutouts', url: 'https://shoutouts.be-infinity.com'},
  {name: 'Daily', url: 'https://daily.be-infinity.com'},
]

const RestrictedScreen: FC<Props> = ({type, onLogout}) => {
  const {t, i18n} = useTranslation()
  const titleKey = type === 'deactivate' ? 'DEACTIVATED_TITLE' : 'RESTRICTED_TITLE'
  const messageKey = type === 'deactivate' ? 'DEACTIVATED_MESSAGE' : 'RESTRICTED_MESSAGE'

  // AuthPage sets this so the branded auth background/theme applies; mirror it here.
  useEffect(() => {
    document.body.classList.add('bg-white')
    return () => {
      document.body.classList.remove('bg-white')
    }
  }, [])

  return (
    <div className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'>
      {/* begin::Language selector */}
      <div className='position-absolute top-0 end-0 p-5'>
        <select
          className='form-select form-select-solid form-select-sm fw-bolder'
          style={{width: '140px'}}
          value={i18n.language}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
        >
          {LANGUAGES.map((lang) => (
            <option value={lang.symbol} key={lang.symbol}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>
      {/* end::Language selector */}

      {/* begin::Content */}
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        {/* begin::Logo */}
        <img alt='Logo' src={toAbsoluteUrl('/media/logos/logo.svg')} className='mb-12 h-80px' />
        {/* end::Logo */}
        {/* begin::Wrapper */}
        <div className='w-lg-600px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>{t(titleKey)}</h1>
            <div className='text-gray-600 fw-semibold fs-5'>{t(messageKey)}</div>
          </div>

          {type === 'restricted' && (
            <div className='mb-10'>
              <div className='text-center text-gray-500 fw-semibold fs-7 text-uppercase mb-4'>
                {t('RESTRICTED_TOOLS_HEADING')}
              </div>
              <div className='row g-3'>
                {TOOLS.map((tool) => (
                  <div className='col-6' key={tool.url}>
                    <a
                      href={tool.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn btn-light-primary w-100 d-flex align-items-center justify-content-between'
                    >
                      <span className='fw-bold'>{tool.name}</span>
                      <i className='bi bi-box-arrow-up-right'></i>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='d-grid'>
            <button type='button' className='btn btn-lg btn-primary' onClick={onLogout}>
              {t('RESTRICTED_SIGNOUT')}
            </button>
          </div>
        </div>
        {/* end::Wrapper */}
      </div>
      {/* end::Content */}
    </div>
  )
}

export default RestrictedScreen
